import { type DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { type DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { Metamodel, DataFlowDSLAdapterRegistry, Owned } from 'shell-models-lib';
import { CassandraSettings } from 'app/model/dataflow/dataset-settings/cassandra-settings';
import { TabularTypeAdapter } from './tabular-type.adapter';
import { ArrayDataFlowType, IntegerDataFlowType, StringDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { DataFlowExpressionUtils } from '../expression.util';
import { DataFlowSchemaAdapterUtils } from '../dataflow-schema-adapter-utils';
import { TableActionOption } from 'app/model/dataflow/dataset-settings/tabular-type-settings';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(CassandraSettings)
})
export class CassandraAdapter extends TabularTypeAdapter {
    public async toSourceDSL(setting: CassandraSettings): Promise<string[]> {
        const props = [];
        props.push(`store: 'cassandra'`);
        if (setting.useQuery) {
            props.push(`format: 'query'`);
            props.push(`query: ${DataFlowExpressionUtils.escapeAndWrapConfigValue(setting.query)}`);
        } else {
            props.push(`format: 'cassandratable'`);
            if (setting.tableName.expression) {
                props.push(`tableName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.tableName)}`);
            }
            if (setting.schemaName.expression) {
                props.push(`keyspace: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.schemaName)}`);
            }
        }

        if (setting.consistencyLevel) {
            props.push(`consistencyLevel: '${DataFlowExpressionUtils.unescapeSingleQuoteInDslString(DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(setting.consistencyLevel))}'`);
        }

        return props;
    }

    public async toSinkDSL(setting: CassandraSettings): Promise<string[]> {
        const props = [];
        props.push(`store: 'cassandra'`);
        props.push(`format: 'cassandratable'`);
        if (setting.tableName.expression) {
            props.push(`tableName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.tableName)}`);
        }
        if (setting.schemaName.expression) {
            props.push(`keyspace: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.schemaName)}`);
        }

        if ((setting.owner as DataFlowSink).requiresKeyColumns) {
            const keyColumns = DataFlowSchemaAdapterUtils.getKeyColumnsProps(setting);
            if (keyColumns) {
                props.push(keyColumns);
            }
        }

        if (setting.tableActionOption === TableActionOption.Recreate) {
            props.push('recreate: true');
        }
        if (setting.tableActionOption === TableActionOption.Truncate) {
            props.push(`truncate: true`);
        }

        if (setting.batchSize.expression) {
            props.push(
                `batchSize: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    setting.batchSize,
                    IntegerDataFlowType.TYPE
                )}`
            );
        }
        if (setting.ttl.expression) {
            props.push(
                `ttl: ${DataFlowExpressionUtils.wrapExpressionConfigValue(setting.ttl, IntegerDataFlowType.TYPE)}`
            );
        }

        if (setting.consistencyLevel) {
            props.push(`consistencyLevel: '${DataFlowExpressionUtils.unescapeSingleQuoteInDslString(DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(setting.consistencyLevel))}'`);
        }

        switch (setting.isPreCqlsListOfExpression) {
            case false:
                if (setting.preCqls && setting.preCqls.length && setting.preCqls[0].expression) {
                    props.push(
                        `preCQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            setting.preCqls[0],
                            ArrayDataFlowType.TYPE
                        )}`
                    );
                }
                break;
            default:
                const preCQLScripts = (setting.preCqls || [])
                    .filter((script) => !!script.expression)
                    .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script))
                    .join(',');
                if (!!preCQLScripts) {
                    props.push(`preCQLs: [${preCQLScripts}]`);
                }
        }
        switch (setting.isPostCqlsListOfExpression) {
            case false:
                if (setting.postCqls && setting.postCqls.length && setting.postCqls[0].expression) {
                    props.push(
                        `postCQLs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                            setting.postCqls[0],
                            ArrayDataFlowType.TYPE
                        )}`
                    );
                }
                break;
            default:
                const postCQLScripts = (setting.postCqls || [])
                    .filter((script) => !!script.expression)
                    .map((script) => DataFlowExpressionUtils.escapeAndWrapConfigValue(script))
                    .join(',');
                if (!!postCQLScripts) {
                    props.push(`postCQLs: [${postCQLScripts}]`);
                }
        }
        return props;
    }

    public async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<CassandraSettings> {
        const settings = this._generateSetting(owner);

        if (configs.has('query')) {
            settings.query = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('query'), owner);
            DataFlowExpressionUtils.unescapeConfigValue(settings.query);
            settings.useQuery = true;
            handledConfigs.push('query');
        }
        if (configs.has('tableName')) {
            settings.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('tableName'), owner);
            settings.useQuery = false;
            handledConfigs.push('tableName');
        }
        if (configs.has('keyspace')) {
            settings.schemaName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('keyspace'), owner);
            settings.useQuery = false;
            handledConfigs.push('keyspace');
            handledConfigs.push('schema');
        } else if (configs.has('schema')) {
            settings.schemaName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('schema'), owner);
            settings.useQuery = false;
            handledConfigs.push('keyspace');
            handledConfigs.push('schema');
        }

        if (configs.has('consistencyLevel')) {
            settings.consistencyLevel = DataFlowExpressionUtils.unescapeSingleQuoteInDslString(DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(configs.get('consistencyLevel')));
            handledConfigs.push('consistencyLevel');
        }

        if (configs.has('inputs')) {
            owner.storedProcInputs = DataFlowExpressionUtils.parseExpressionMapInDsl(
                configs.get('keyspace'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('inputs');
        }

        return settings;
    }

    public async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<CassandraSettings> {
        const settings = this._generateSetting(owner);
        settings.useQuery = false;
        if (configs.has('tableName')) {
            settings.tableName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('tableName'), owner);
            handledConfigs.push('tableName');
        }
        if (configs.has('keyspace')) {
            settings.schemaName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('keyspace'), owner);
            handledConfigs.push('keyspace');
            handledConfigs.push('schema');
        } else if (configs.has('schema')) {
            settings.schemaName = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('schema'), owner);
            handledConfigs.push('keyspace');
            handledConfigs.push('schema');
        }

        if (configs.has('keys')) {
            const expression = configs.get('keys');
            settings.isListOfKeyColumns = expression.startsWith('[') && expression.endsWith(']');
            settings.keyColumns = settings.isListOfKeyColumns
                ? DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner, StringDataFlowType.TYPE, true)
                : [DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, 'string[]', true)];
            handledConfigs.push('keys');
        }

        if (configs.has('truncate')) {
            settings.tableActionOption =
                configs.get('truncate') === 'true' ? TableActionOption.Truncate : settings.tableActionOption;
            handledConfigs.push('truncate');
        }
        if (configs.has('recreate')) {
            settings.tableActionOption =
                configs.get('recreate') === 'true' ? TableActionOption.Recreate : settings.tableActionOption;
            handledConfigs.push('recreate');
        }

        if (configs.has('batchSize')) {
            settings.batchSize = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('batchSize'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('batchSize');
        }
        if (configs.has('ttl')) {
            settings.ttl = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('ttl'),
                owner,
                IntegerDataFlowType.TYPE
            );
            handledConfigs.push('ttl');
        }

        if (configs.has('consistencyLevel')) {
            settings.consistencyLevel = DataFlowExpressionUtils.unescapeSingleQuoteInDslString(DataFlowExpressionUtils.trimSpacesAndQuotesInDslString(configs.get('consistencyLevel')));
            handledConfigs.push('consistencyLevel');
        }

        if (configs.has('preCQLs')) {
            const expression = configs.get('preCQLs');
            if (expression.startsWith('[') && expression.endsWith(']')) {
                settings.isPreCqlsListOfExpression = true;
                const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner);
                if (expressionArray.length > 0) {
                    settings.preCqls = expressionArray.map((expression) => {
                        if (!expression.templateExpression) {
                            DataFlowExpressionUtils.unescapeConfigValue(expression);
                        }
                        return expression;
                    });
                }
            } else {
                settings.isPreCqlsListOfExpression = false;
                settings.preCqls = [
                    DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, ArrayDataFlowType.TYPE)
                ];
            }
            handledConfigs.push('preCQLs');
        }
        if (configs.has('postCQLs')) {
            const expression = configs.get('postCQLs');
            if (expression.startsWith('[') && expression.endsWith(']')) {
                settings.isPostCqlsListOfExpression = true;
                const expressionArray = DataFlowExpressionUtils.parseExpressionArrayInDsl(expression, owner);
                if (expressionArray.length > 0) {
                    settings.postCqls = expressionArray.map((expression) => {
                        if (!expression.templateExpression) {
                            DataFlowExpressionUtils.unescapeConfigValue(expression);
                        }
                        return expression;
                    });
                }
            } else {
                settings.isPostCqlsListOfExpression = false;
                settings.postCqls = [
                    DataFlowExpressionUtils.unwrapExpressionConfigValue(expression, owner, ArrayDataFlowType.TYPE)
                ];
            }
        }
        return settings;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): CassandraSettings {
        const result = Owned.create<CassandraSettings>(CassandraSettings.MODEL, owner);
        result.initialize(undefined);
        return result;
    }
}
